<template>
  <div>
    <h1 class="mb-30">Сертификаты</h1>

    <!-- Панель с кнопками фильтра и создания сертификата -->
    <div class="between ai-c mb-30">
      <VButton
        class="mb-30"
        size="menu"
        icon="filter"
        color="primary-sm"
        :active="isFilter"
        @click.native="openFilter"
      />

      <VButton
        v-if="permissionUpdateWarehouseCertificate && !isMobile"
        text="Создать"
        size="md"
        icon="create"
        color="primary-sm"
        @click.native="openModalHandler('upsertCertificate')"
      />
    </div>

    <!-- Фильтр сертификатов -->
    <transition name="fade">
      <CertificatesFilter class="mb-30" v-if="isFilter && !isMobile" @loadList="loadList" />
    </transition>
    <BaseModal
      v-if="isModal === 'filter'"
      class="modal--sm modal--filter"
      :hidden-close-btn="true"
      @close="closeModal"
    >
      <CertificatesFilter @closeModal="closeModal"  @loadList="loadList" />
    </BaseModal>

    <div>
      <Spinner v-if="isLoading"/>

      <!-- Основная таблица сертификатов (древовидная) -->
      <AppTable
        v-if="certificatesLength && !isLoading"
        class="certificates-table"
        is-tree-table
        is-sticky
        :columns="columns"
        :items="certificates"
        :tree-children-keys="treeChildrenKeys"
        :is-open-tree="false"
        :loading="isLoading"
        :show-action-column="!isMobile && permissionUpdateWarehouseCertificate"
        :show-actions-rule="shouldShowActions"
      >
        <!-- Кастомный рендер ячеек: для файлов используем AttachmentFilesList -->
        <template #cell="{ item, col, lvl }">
          <AttachmentFilesList
            v-if="lvl === 3 && col.name === 'name'"
            :files="[item]"
            is-enabled-download-file
            with-preview
            show-date
          />
            <span v-else>
              {{ lvl === 1 ? item[col.name] : (item['d_wire'] + ' мм') }}
            </span>
        </template>
        <!-- Кнопки действий для разных уровней дерева -->
        <template #actionButton="{ item, lvl, parentItem }">
          <!-- material level -->
          <div v-if="lvl === 1" class="d-flex gap-10">
            <VButton
              icon="remove"
              size="md"
              @click.native="openModalHandler('deleteCertificate', item)" />
            <VButton
              icon="edit"
              size="md"
              @click.native="openModalHandler('upsertCertificate', item)" />
          </div>
          <!-- d_wire level -->
          <div v-if="lvl === 2" class="d-flex gap-10">
            <VButton
              icon="remove"
              size="md"
              @click.native="openModalHandler('deleteDWire', item)" />
            <VButton
              icon="create"
              size="md"
              color="primary-sm"
              @click.native="openModalHandler('certificateAddFiles', item)" />
          </div>
          <!-- file level -->
          <div v-if="lvl === 3" class="d-flex gap-10">
            <VButton
              icon="remove"
              size="md"
              @click.native="openModalHandler('deleteFile', item)" />
            <VButton
              size="md"
              icon="refresh"
              color="primary-sm"
              @click.native="openModalHandler('certificateChangeFile', item, parentItem)" />
          </div>
        </template>
      </AppTable>

      <h2 v-if="!certificatesLength && !isLoading">
        Ничего не найдено
      </h2>
    </div>

    <!-- Модалки для создания/редактирования сертификата -->
    <ModalCertificateUpsert
      v-if="isModal === 'upsertCertificate'"
      :certificate="selectedItem"
      @loadList="loadList"
      @reject="closeModalCertificate"
    />

    <!-- Модалка для добавления файлов к сертификату -->
    <ModalCertificateFile
      v-if="isModal === 'certificateAddFiles'"
      mode="add"
      :item="selectedItem"
      :action="addCertificateFile"
      @loadList="loadList"
      @reject="closeModalCertificate"
    />

    <!-- Модалка для замены файла -->
    <ModalCertificateFile
      v-if="isModal === 'certificateChangeFile'"
      mode="change"
      title="Обновить"
      :item="selectedItem"
      :parent-item="parentItem"
      :action="changeCertificateFile"
      @loadList="loadList"
      @reject="closeModalCertificate"
    />

    <ModalConfirmation
      v-if="isModal === 'deleteFile' || isModal === 'deleteDWire' || isModal === 'deleteCertificate'"
      title="Удалить выбранный элемент?"
      @confirm="certificateDeleteHandler"
      @reject="closeModalCertificate"
    />
  </div>
</template>
<script>
import VButton from '@/components/simple/button/VButton.vue'
import AppTable from '@/components/ui/AppTable.vue'
import BaseModal from '@/components/ui/Modal/BaseModal.vue'
import { mixinModal } from '@/mixins/modal/mixinModal'
import { mixinIsFilter } from '@/mixins/mixinIsFilter'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import CertificatesFilter from '@/components/warehouse/certificates/CertificatesFilter.vue'
import ModalCertificateUpsert from '@/components/warehouse/certificates/ModalCertificateUpsert.vue'
import AttachmentFilesList from '@/components/files/AttachmentFiles/AttachmentFilesList'
import ModalCertificateFile from '@/components/warehouse/certificates/ModalCertificateFile.vue'
import ModalConfirmation from '@/components/ui/Modal/ModalConfirmation.vue'
import { setAlertError } from '@/utils/store/alert'

export default {
  name: 'Certificates',
  components: {
    ModalConfirmation,
    AttachmentFilesList,
    ModalCertificateFile,
    ModalCertificateUpsert,
    CertificatesFilter,
    BaseModal,
    VButton,
    AppTable
  },
  mixins: [mixinModal, mixinIsFilter],
  data () {
    return {
      // Ключи для построения дерева в таблице
      treeChildrenKeys: {
        category: 'd_wires',
        categoryItem: 'files'
      },
      // Описание колонок таблицы
      columns: [
        {
          title: 'Материал и d проволоки',
          name: 'name',
          routerData: {
            pageName: 'OrderDetails',
            dataKey: 'account_number',
            textLink: 'заказ'
          }
        }
      ],
      // Текущий выбранный элемент (сертификат, d_wire или файл)
      selectedItem: null,
      // Родитель текущего выбранного элемента (сертификат или d_wire)
      parentItem: null
    }
  },

  mounted () {
    this.getCertificatesTree()
    this.getCertificatesFilters()
  },

  beforeDestroy () {
    this.RESET_FILTER()
  },

  computed: {
    ...mapGetters('warehouse', {
      certificates: 'certificates/certificatesTree',
      isLoading: 'certificates/isLoading'
    }),
    ...mapGetters('permission', {
      permissionUpdateWarehouseCertificate: 'permissionUpdateWarehouseCertificate'
    }),
    ...mapState('mobile', {
      isMobile: 'isMobile'
    }),

    certificatesLength () {
      return this.certificates?.length
    }
  },

  methods: {
    ...mapActions('warehouse/certificates', [
      'getCertificatesTree',
      'getCertificatesFilters',
      'deleteCertificate',
      'deleteCertificateDWire',
      'deleteCertificateFile',
      'addCertificateFile',
      'changeCertificateFile'
    ]),

    ...mapMutations('warehouse', {
      RESET_FILTER: 'certificates/RESET_FILTER'
    }),

    /**
     * Определяет, показывать ли action-кнопки для строки таблицы.
     * Для первого и второго уровня (ГОСТ и d проволоки) — только когда он раскрыт или не имеет дочерних элементов.
     * Для файлов — всегда.
     */
    shouldShowActions ({ lvl, isCategory, isOpenChild }) {
      if (lvl === 1 || lvl === 2) {
        return !isCategory || isOpenChild
      }
      return true
    },

    /**
     * Перезагрузка списка сертификатов
     */
    loadList () {
      this.getCertificatesTree()
    },

    /**
     * Универсальный обработчик открытия модалок
     * @param {string} type - тип модалки ('upsertCertificate', 'deleteFile', и т.д.)
     * @param {Object|null} item - выбранный элемент (сертификат, d_wire или файл)
     * @param {Object|null} parentItem - родитель выбранного элемента (актуально для файлов)
     */
    openModalHandler (type, item = null, parentItem = null) {
      this.selectedItem = item
      this.parentItem = parentItem
      this.openModal(type)
    },

    /**
     * Закрытие модалки и сброс выбранных элементов
     */
    closeModalCertificate () {
      this.closeModal()
      this.selectedItem = null
      this.parentItem = null
    },

    /**
     * Обработчик подтверждения удаления (вызывается из модалки подтверждения).
     * В зависимости от типа удаляет сертификат, d_wire или файл.
     */
    async certificateDeleteHandler () {
      try {
        let response, params
        switch (this.isModal) {
          case 'deleteFile':
            response = await this.deleteCertificateFile(this.selectedItem.id)
            break
          case 'deleteDWire':
            params = {
              name: this.selectedItem.name,
              d_wire: this.selectedItem.d_wire
            }
            response = await this.deleteCertificate(params)
            break
          case 'deleteCertificate':
            params = {
              name: this.selectedItem.name
            }
            response = await this.deleteCertificate(params)
            break
        }

        if (response?.status === 204 || response?.status === 200) {
          this.closeModal()
          this.loadList()
        }
      } catch (e) {
        await setAlertError(e, 'Не удалось удалить элемент')
      }
    }
  }
}
</script>

<style lang="scss">
.certificates-table {
  & .attachment-files {
    min-width: 400px;
  }
}
</style>
